import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Form } from 'react-bootstrap';
import { sprayService, SprayProgram, SprayProduct } from '../../services/sprayService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar } from '../patterns';
import { ProgramStatusPill } from './statusPills';
import ProgramActionsMenu from './ProgramActionsMenu';
import ProgramDetailModal from './ProgramDetailModal';
import IdChip from './IdChip';
import SprayProgramRoundsPanel, { ConvertScope } from './SprayProgramRoundsPanel';
import RoundConvertModal from './RoundConvertModal';
import BulkActionToolbar, { BulkAction } from '../common/BulkActionToolbar';

interface Props {
  onNewProgram?: () => void;
  onEditProgram?: (program: SprayProgram) => void;
  onGenerateInstruction?: (programId: string) => void;
  onCloneProgram?: (programId: string) => void;
  onUnlockProgram?: (programId: string) => void;
  onRestoreProgram?: (programId: string) => void;
  products?: SprayProduct[];
}

type StatusFilter = 'all' | 'open' | 'locked' | 'deleted';

const SprayProgramList: React.FC<Props> = ({
  onNewProgram,
  onEditProgram,
  onGenerateInstruction,
  onCloneProgram,
  onUnlockProgram,
  onRestoreProgram,
  products,
}) => {
  const { t } = useTranslation();
  const [showDeleted, setShowDeleted] = useState(false);
  const [programs, setPrograms] = useState<SprayProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fetchKey, setFetchKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailProgram, setDetailProgram] = useState<SprayProgram | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Phase 3: per-program expansion state for the inline rounds panel.
  // Tracking by programId so multiple programs can stay expanded.
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());

  // Convert flow state. `convertContext` carries the program + scope so we
  // can dispatch single vs bulk + render the right modal title.
  const [convertContext, setConvertContext] = useState<{ program: SprayProgram; scope: ConvertScope } | null>(null);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [toast, setToast] = useState<{ created: number; skipped: number } | null>(null);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.programs.title') },
  ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    sprayService.getPrograms({ status: showDeleted ? 'all' : undefined })
      .then(data => { if (!cancelled) { setPrograms(data); setSelectedIds(new Set()); } })
      .catch(() => { if (!cancelled) setError(t('spray.programs.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted, fetchKey]);

  const toggleSelected = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Phase 3 (track session_delete_bulk_actions_20260426 spec §4): selection
  // covers ALL visible rows, including deleted ones when 'Show deleted' is ON,
  // so the user can bulk-restore. The shared <BulkActionToolbar> uses
  // applicableWhen predicates to scope each action to its relevant subset.
  // After Task 11, "visible" reflects client-side filters too — so search and
  // chip filters narrow the select-all target as well, which is what users
  // expect when typing into the search box.
  const selectedPrograms = programs.filter(p => selectedIds.has(p._id));

  const toggleExpandedProgram = (id: string) => {
    setExpandedPrograms(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /**
   * Round-conversion request from a program's inline panel. Stores the
   * program + scope so the orchard-picker modal can render the right title
   * and dispatch the right service call on confirm.
   */
  const handleRequestConvert = (program: SprayProgram, scope: ConvertScope) => {
    setConvertError('');
    setConvertContext({ program, scope });
  };

  const closeConvertModal = () => {
    setConvertContext(null);
    setConvertError('');
    setConverting(false);
  };

  /**
   * Confirms the convert action. Single-round vs bulk dispatch is decided
   * here so the modal stays decoupled from the response-shape difference
   * (single 409 vs bulk 201). Any 409 on a single-orchard pick surfaces
   * as a localized error message inside the modal so the user can see the
   * existing session reference; otherwise we close the modal and show a
   * summary toast.
   */
  const handleConvertConfirm = async (orchardIds: string[]) => {
    if (!convertContext) return;
    const { program, scope } = convertContext;
    setConverting(true);
    setConvertError('');
    try {
      if (scope.kind === 'single') {
        const res = await sprayService.generateSessionsForRound(
          program._id, scope.roundId, { orchardIds },
        );
        if (res.status === 409) {
          // Spec §2.4 — single-convert with all orchards already done.
          // Pull the first existing-session ref into a localized message
          // so the operator can navigate to it manually.
          const ref = res.skipped[0]?.existingSessionId || '';
          setConvertError(t('spray.programs.convertSingleConflict', { sessionId: ref.slice(-6) || '?' }));
          setConverting(false);
          return;
        }
        setToast({ created: res.created.length, skipped: res.skipped.length });
      } else {
        const roundIds = scope.kind === 'all' ? 'all' : scope.roundIds;
        const res = await sprayService.bulkSessionsFromRounds(
          program._id, { roundIds, orchardIds },
        );
        setToast({ created: res.created.length, skipped: res.skipped.length });
      }
      setFetchKey(k => k + 1); // refresh program list (status may have flipped to locked)
      closeConvertModal();
    } catch (err: any) {
      setConvertError(err.response?.data?.error || err.message || t('spray.programs.convertError'));
      setConverting(false);
    }
  };

  const convertModalTitle = (() => {
    if (!convertContext) return '';
    const { scope } = convertContext;
    if (scope.kind === 'single')   return t('spray.programs.convertSingleTitle',   { name: scope.roundLabel });
    if (scope.kind === 'selected') return t('spray.programs.convertSelectedTitle', { count: scope.count });
    return t('spray.programs.convertAllTitle', { count: scope.count });
  })();

  const handleDeleteSingle = async (id: string) => {
    if (!window.confirm(t('spray.programs.confirmDelete'))) return;
    try {
      await sprayService.deleteProgram(id);
      setFetchKey(k => k + 1);
    } catch {
      setError(t('spray.programs.deleteError'));
    }
  };

  const bulkActions: BulkAction<SprayProgram>[] = [
    {
      key: 'bulk-delete',
      label: t('spray.programs.bulkDelete'),
      variant: 'danger',
      applicableWhen: p => (p.status || 'open') !== 'deleted',
      onClick: async items => {
        try {
          await sprayService.bulkDeletePrograms(items.map(p => p._id));
          setSelectedIds(new Set());
          setFetchKey(k => k + 1);
        } catch {
          setError(t('spray.programs.deleteError'));
        }
      },
      confirm: {
        title: t('spray.programs.confirmBulkDeleteTitle'),
        body: count => t('spray.programs.confirmBulkDeleteBody', { count }),
        confirmLabel: t('spray.programs.bulkDelete'),
        variant: 'danger',
      },
    },
    {
      key: 'bulk-restore',
      label: t('spray.programs.bulkRestore'),
      variant: 'success',
      applicableWhen: p => p.status === 'deleted',
      onClick: async items => {
        try {
          await sprayService.bulkRestorePrograms(items.map(p => p._id));
          setSelectedIds(new Set());
          setFetchKey(k => k + 1);
        } catch {
          setError(t('spray.programs.restoreError'));
        }
      },
      confirm: {
        title: t('spray.programs.confirmBulkRestoreTitle'),
        body: count => t('spray.programs.confirmBulkRestoreBody', { count }),
        confirmLabel: t('spray.programs.bulkRestore'),
        variant: 'success',
      },
    },
  ];

  // Client-side narrowing — search + status chips. Note: when showDeleted is
  // OFF the server already withholds deleted rows, so the chip set
  // (All/Open/Locked) is sufficient. Deleted rows show only when the toggle
  // is ON and the user picks "All" in the chip filter.
  const visiblePrograms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return programs.filter(p => {
      if (statusFilter !== 'all') {
        if ((p.status || 'open') !== statusFilter) return false;
      }
      if (q) {
        if (!p.name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [programs, search, statusFilter]);

  const allVisibleSelected = visiblePrograms.length > 0
    && visiblePrograms.every(p => selectedIds.has(p._id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      // Clear selection only for currently-visible rows — preserves any
      // selections that exist on rows hidden by an active client-side filter.
      setSelectedIds(prev => {
        const next = new Set(prev);
        visiblePrograms.forEach(p => next.delete(p._id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        visiblePrograms.forEach(p => next.add(p._id));
        return next;
      });
    }
  };

  const counts = useMemo(() => ({
    all: programs.length,
    open: programs.filter(p => (p.status || 'open') === 'open').length,
    locked: programs.filter(p => p.status === 'locked').length,
    deleted: programs.filter(p => p.status === 'deleted').length,
  }), [programs]);

  const chips = [
    { id: 'all',    label: t('spray.filters.all'),    count: counts.all,    active: statusFilter === 'all' },
    { id: 'open',   label: t('spray.filters.open'),   count: counts.open,   active: statusFilter === 'open' },
    { id: 'locked', label: t('spray.filters.locked'), count: counts.locked, active: statusFilter === 'locked' },
  ];
  if (showDeleted) {
    chips.push({
      id: 'deleted',
      label: t('spray.filters.deleted'),
      count: counts.deleted,
      active: statusFilter === 'deleted',
    });
  }

  return (
    <div data-testid="spray-program-list">
      <PageHeader
        title={t('spray.programs.title')}
        meta={t('spray.programs.metaCount', { count: visiblePrograms.length })}
        actions={
          <div className="d-flex align-items-center gap-3">
            <Form.Check
              type="switch"
              id="show-deleted-toggle"
              label={t('spray.programs.showDeleted')}
              checked={showDeleted}
              onChange={() => {
                setShowDeleted(prev => {
                  // When turning showDeleted OFF, reset a 'deleted' chip filter
                  // so the user isn't left with an empty/inconsistent view.
                  if (prev) setStatusFilter(sf => sf === 'deleted' ? 'all' : sf);
                  return !prev;
                });
              }}
            />
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={() => onNewProgram?.()}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.pageHeader.actions.newProgram')}
            </button>
          </div>
        }
      />

      {error && <Alert variant="danger" role="alert" className="mb-3">{error}</Alert>}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('spray.programs.searchPlaceholder')}
        chips={chips}
        onChipClick={(id) => setStatusFilter(id as StatusFilter)}
      />

      {/* Select-all + bulk-action toolbar row, sits between FilterBar and DataTable. */}
      {!loading && programs.length > 0 && (
        <div className="d-flex align-items-center gap-3 mb-2 px-2">
          <label className="d-inline-flex align-items-center gap-2 mb-0">
            <input
              type="checkbox"
              className="pat-checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              aria-label={t('spray.programs.selectAllPrograms')}
            />
            <span className="text-muted small">
              {t('spray.programs.selectAll')}
            </span>
          </label>
          <BulkActionToolbar
            selectedItems={selectedPrograms}
            actions={bulkActions}
            onClearSelection={() => setSelectedIds(new Set())}
            clearLabel={t('spray.programs.clearSelection')}
          />
        </div>
      )}

      {loading && <div>{t('common.loading')}</div>}

      {!loading && !error && (
        <DataTable<SprayProgram>
          rows={visiblePrograms}
          rowKey={(p) => p._id}
          rowClassName={(p) => (p.status === 'deleted' ? 'text-muted' : '')}
          expandedRowKey={expandedPrograms}
          expandedRowRender={(p) => {
            const status = p.status || 'open';
            if (status === 'deleted') return null;
            return (
              <SprayProgramRoundsPanel
                program={p}
                products={products || []}
                onRequestConvert={(scope) => handleRequestConvert(p, scope)}
              />
            );
          }}
          columns={[
            {
              key: 'select',
              label: '',
              render: (p) => (
                <input
                  type="checkbox"
                  className="pat-checkbox"
                  checked={selectedIds.has(p._id)}
                  onChange={(e) => { e.stopPropagation(); toggleSelected(p._id); }}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t('spray.programs.selectProgram', { name: p.name })}
                />
              ),
            },
            {
              key: 'name',
              label: t('spray.programs.name'),
              render: (p) => (
                <span>
                  {p.name}
                  <IdChip prefix="PGM" seq={p.seq} />
                </span>
              ),
            },
            {
              key: 'plannedDate',
              label: t('spray.programs.plannedDate'),
              render: (p) => p.plannedDate?.substring(0, 10) ?? '',
            },
            {
              key: 'status',
              label: t('spray.programs.status'),
              render: (p) => <ProgramStatusPill status={p.status || 'open'} />,
            },
            {
              key: 'rounds',
              label: t('spray.programs.rounds'),
              render: (p) => {
                const status = p.status || 'open';
                const isDeleted = status === 'deleted';
                const isExpanded = expandedPrograms.has(p._id);
                return (
                  <button
                    type="button"
                    className="pat-btn pat-btn--subtle"
                    onClick={(e) => { e.stopPropagation(); toggleExpandedProgram(p._id); }}
                    aria-label={t(
                      isExpanded ? 'spray.programs.collapseProgram' : 'spray.programs.expandProgram',
                      { name: p.name }
                    )}
                    aria-expanded={isExpanded}
                    disabled={isDeleted}
                  >
                    {(p.rounds || []).length}{' '}
                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden="true" />
                  </button>
                );
              },
            },
            {
              key: 'sessionsColumn',
              label: t('spray.programs.sessionsColumn'),
              render: (p) => (
                <Link to={`/protection/sessions?programId=${p._id}`}>
                  {t('spray.programs.viewSessions', { count: (p.sessionNames || []).length })}
                </Link>
              ),
            },
            {
              key: 'actions',
              label: '',
              align: 'right',
              render: (p) => (
                <ProgramActionsMenu
                  program={p}
                  onEdit={() => onEditProgram?.(p)}
                  onClone={() => onCloneProgram?.(p._id)}
                  onConvert={() => handleRequestConvert(p, { kind: 'all', count: (p.rounds || []).length })}
                  onDelete={() => handleDeleteSingle(p._id)}
                  onViewDetails={() => setDetailProgram(p)}
                  onUnlock={() => onUnlockProgram?.(p._id)}
                  onRestore={() => onRestoreProgram?.(p._id)}
                />
              ),
            },
          ]}
          emptyState={<div className="p-4 text-muted">{t('spray.programs.noPrograms')}</div>}
        />
      )}

      {detailProgram && (
        <ProgramDetailModal
          program={detailProgram}
          products={products || []}
          show={!!detailProgram}
          onHide={() => setDetailProgram(null)}
        />
      )}

      {convertContext && (
        <RoundConvertModal
          show={!!convertContext}
          title={convertModalTitle}
          submitting={converting}
          errorMessage={convertError}
          onConfirm={handleConvertConfirm}
          onCancel={closeConvertModal}
        />
      )}

      {toast && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setToast(null)}
          className="position-fixed bottom-0 end-0 m-3 shadow"
          role="status"
          style={{ zIndex: 1080 }}
        >
          {t('spray.programs.convertSummaryToast', toast)}
        </Alert>
      )}
    </div>
  );
};

export default SprayProgramList;
